import '../product-card/product-card-display';
import './content-card-display';
import emit from '../../common/events';
import searchIcon from '../../common/icons/search-icon';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { Catalog } from '../../common/constants/catalog';
import {
  DISCIPLINE_PRODUCT_TAG_PATH,
  getSearchRequestFromUrl,
  performSearch,
  performSearchSpecificIsbn,
} from './thunks/search.thunk';
import { PredefinedFilter, SearchFacet } from './interfaces/search.interface';
import { StoreState } from '../../store/store.interface';
import { Suggestion, Suggestions, TopCategoryFacetValue } from './interfaces/suggestion.interface';
import { booleanConverter } from '../../common/converters';
import { classMap } from 'lit-html/directives/class-map.js';
import { clearSuggestions } from './store/suggestion.store';
import { connect } from 'pwa-helpers';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getSuggestions } from './thunks/suggestion.thunk';
import { getUniqueId, mapCatalog } from '../../common/utils/util';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { isHeCustomStoreCart, isHePrivateStoreCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { isLoaded, selectSuggestions } from './store/suggestion.selector';
import { selectFacets, selectSelectedCatalog, selectSortBy } from './store/search.selector';
import { setPredefinedFilters, setSearchPageType, setSelectedCatalog } from './store/search.store';
import { smallSpinner } from '../../common/constants/api-constants';
import debounce from 'lodash/debounce';
import { generateSearchUrl, savePdpLocalStorageObject, saveSearchLocalStorageObject } from './utils/search.util';
import { productDiscoveryQPKey, productDiscoveryQPValue, SearchTermAnalyticsConstants } from '../../common/constants/anayltics.constants';
import { appendQueryParamToUrl, checkBUParam, getSearchPageAnalytics } from '../../common/utils/analytics-util';

declare global {
  interface Window {
    location: Location;
  }
}

const catalogs = ['he', 'seg', 'content', 'products', 'articles'];

export enum Size {
  SMALL = 'small',
  DEFAULT = 'default',
  LARGE = 'large',
}

interface SearchTermRedirect {
  searchTerm: string;
  target: string;
  catalog: string;
}

export interface SearchTermRedirects {
  redirects: Array<SearchTermRedirect>;
}

export const DEFAULT_SEARCH_TERM_REDIRECTS: SearchTermRedirects = {
  redirects: [],
};

/**
 * An element to search for products
 *
 * @event click - A user has clicked something within the search component.
 *        <br><br>&cross; dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: n/a
 * @event searched - A searched with their own search term.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 * @event searched-for-category-suggestion - A user clicked on a category suggestion.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 * @event searched-for-term-suggestion - A user clicked on a term suggestion.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { term }
 */
@customElement('mhecomm-search')
export class Search extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .dropdown-menu {
        z-index: 1050 !important;
      }
      .dropdown-item:hover, .dropdown-item:focus, .dropdown-item.bg-light {
        font-weight: bold;
      }
    `,
  ];
  /**
   * The text to display when there is at least one item in the cart.
   */
  @property()
  placeholder = 'Search by ISBN, Title, Author, Keyword';

  /**
   * The search query in the input field
   */
  @property()
  searchQuery = '';

  /**
   * set isLoading on search click to show loader on icon
   */
  @state()
  private isLoading = false;

  /**
   * The search query in the input field
   */
  @property({ converter: booleanConverter })
  autoLoad = false;

  /**
   * The number of results to load. Only used when loadResults is true.
   */
  @property({ type: Number })
  pageSize = 10;

  /**
   * The number of results to load. Only used when loadResults is true.
   */
  @property()
  size = Size.DEFAULT;

  /**
   * Render the search component in light mode.
   */
  @property({ converter: booleanConverter })
  lightMode = false;

  /**
   * The catalog to search within. One of the following values: unified, he, seg, seg-store, and content. seg-store cannot be used in conjunction with useSuggestions="true".
   */
  @property()
  catalog = 'unified';

  /**
   * The store to search within.
   */
  @property()
  store: string | undefined;

  /**
   * Whether the suggestions should be open or not
   */
  @property({ converter: booleanConverter })
  suggestionsOpen = false;

  /**
   * Whether or not the suggestion dropdown should be active
   */
  @property({ converter: booleanConverter })
  useSuggestions = true;

  /**
   * Whether or not the search results should be loaded. If this is true, the user will not be navigated to the search results page after performing a search. If a search results component is on the page, that component will load in the search results.
   */
  @property({ converter: booleanConverter })
  loadResults = false;

  /**
   * A json object configuring search term redirects. If the user searches on one of the provided search terms
   * the user will be redirected to the configured page instead of doing a normal search.
   */
  @property({ type: Object })
  searchTermRedirects: SearchTermRedirects = DEFAULT_SEARCH_TERM_REDIRECTS;

  /**
   * is plp search within or not
   */
  @property({ converter: booleanConverter })
  plpSearch = false;

  /**
   * Parameters to add predefined filters to the search.
   */
  @property({ type: Array })
  predefinedFilters: Array<PredefinedFilter> = [];

  /**
   * Parameter to determine the page the search component is getting called, default is empty string
   */
  @property({ type: String })
  searchPageType = '';

  // Defaults to true, and then the stateChanged will update this as needed
  @state()
  private hide = true;

  @state()
  private suggestions: Suggestions | undefined;

  /**
   * focus index of the catalog menu item focused using arrow keys
   */
  @state()
  private currentFocus = -1;

  /**
   * get search selected catalog
   */
  @state()
  private selectedCatalog;

  /**
   * set isSearchSubmit on button click to show suggestions or not
   */
  @state()
  private isSearchSubmit = false;

  /**
   * Tracks if a selection has been made from the suggestion dropdown
   */
  @state()
  private hasSelectionBeenMade = false;

  /**
   * Text to describe mhecomm-search
   */
  @property()
  ariaDescriptionText =
    'When autocomplete catalog suggestions are available use up and down arrows to review and enter to select.';

  _id = getUniqueId();

  connectedCallback(): void {
    super.connectedCallback();

    this._addCloseSuggestionsListeners();

    if (this.predefinedFilters.length > 0) {
      store.dispatch(setPredefinedFilters(this.predefinedFilters));
    }

    if (this.searchPageType) {
      store.dispatch(setSearchPageType(this.searchPageType));
    }
    
    if (this.autoLoad && this.loadResults) {
      this._search(true);
    }

    this.addEventListener('input', debounce(this._inputUpdate, 500));
    this.addEventListener('input', this._updateSearchQuery);

    this._prePopulateSearchLSValues();
  }

  disconnectedCallback(): void {
    this.removeEventListener('input', debounce(this._inputUpdate, 500));
    this.removeEventListener('input', this._updateSearchQuery);
  }

  renderComp() {
    return html`
      <div class="w-100 input-group ${this._getInputGroupClasses()}" tabindex="-1" role="region" aria-label="Search">
        <input
          aria-label="search"
          id="input-search-box"
          data-automation-id="input-search-box"
          class="rounded-start form-control ms-0 ${this._getInputClasses()}"
          type="text"
          placeholder=${this.placeholder}
          .value=${this.searchQuery}
          @focus=${this._focus}
          @keypress=${this._keyPress}
          @keydown=${this._catalogMenuKeyboardNav}
          aria-controls=${ifDefined(this.useSuggestions ? `${this.id}-autocomplete-catalog-menu` : undefined)}
          aria-activedescendant=${ifDefined(this.currentFocus >= 0 ? `active-option-${this.currentFocus}` : undefined)}
          aria-autocomplete=${ifDefined(this.useSuggestions ? 'list' : undefined)}
          role=${ifDefined(this.useSuggestions ? 'combobox' : undefined)}
          aria-haspopup=${ifDefined(this.useSuggestions ? 'listbox' : undefined)}
          aria-expanded=${ifDefined(this.useSuggestions ? `${this.suggestionsOpen}` : undefined)}
        />
        <button
          id="submit-search-button"
          data-automation-id="submit-search-button"
          class="btn ${this._getButtonClass()}"
          aria-label="Search Submit"
          @click=${() => this._search(false, true)}
        >
          ${this.isLoading ? smallSpinner() : searchIcon}
        </button>
      </div>
      <div class="dropdown">
        <ul
          class="dropdown-menu position-relative position-xl-absolute w-100 ${this._suggestionClasses()}"
          id="${this.id}-autocomplete-catalog-menu"
          role="listbox"
        >
          ${this.suggestions?.suggestions?.map(
            (suggestion, index) => html`
              <li role="presentation">
                <a
                  id="active-option-${index}"
                  class="dropdown-item text-wrap"
                  href="${this._termSuggestionUrl(suggestion)}"
                  role="option"
                  aria-selected="false"
                  tabindex="-1"
                  @click=${(e) => this._searchForTermSuggestion(e, suggestion.terms)}
                >
                  ${suggestion.terms}
                </a>
              </li>
            `
          )}
        </ul>
      </div>
    `;
  }

  private _getInputClasses() {
    return classMap({
      'border-0': this.lightMode,
      'search-input-light': this.lightMode,
    });
  }

  private _getInputGroupClasses() {
    return classMap({
      'd-none': this.hide,
      'input-group-sm': this.size === Size.SMALL,
      'input-group-lg': this.size === Size.LARGE,
    });
  }

  private _getButtonClass() {
    return classMap({
      'btn-primary': !this.lightMode,
      'btn-light': this.lightMode,
    });
  }

  /**
   * Adds 'focusout' and 'click' event listeners to the body, as 'focusin' doesn't work properly on Safari, causing result links to not work
   */
  private _addCloseSuggestionsListeners() {
    ['focusout', 'click'].forEach((event) =>
      document.body.addEventListener(event, (evt: any) => this._closeSuggestions(evt))
    );
  }

  private _closeSuggestions(event: any) {
    if (!event.composedPath().includes(this)) {
      this.suggestionsOpen = false;
      this.currentFocus = -1; // Reset focus when closing suggestions
    }
  }

  private _suggestionClasses() {
    return classMap({
      show: this.suggestionsOpen && (this.hasSuggestions() || this.hasFacetValueSuggestions()),
    });
  }

  private _setSearchPlaceholder() {
    if (this.selectedCatalog) {
      this.placeholder = {
        [Catalog.unified]: 'Search by ISBN, Title, Author, Keyword',
        [Catalog.seg]: 'Search by ISBN, MHID, Title, State or Subject',
        [Catalog.he]: 'Search by ISBN, Title, Author, Keyword',
        [Catalog.content]: 'Search for Topics & Ideas',
        [Catalog.products]: 'Search by ISBN, Title, Author, Keyword',
        [Catalog.articles]: 'Search by Title, Author, Keyword',
      }[this.selectedCatalog];
    }
  }

  stateChanged(state: StoreState) {
    if (this.loadResults) {
      this.selectedCatalog = selectSelectedCatalog(state);
      if (this.selectedCatalog) {
        this.catalog = this.selectedCatalog;
      }
    }

    this.suggestions = selectSuggestions(state, this._id);

    if ((this === document.activeElement || this._hasElementFocus()) && isLoaded(state, this._id) && !this.hasSelectionBeenMade) {
      this.suggestionsOpen = true;
    }

    if (this.isSearchSubmit || this.hasSelectionBeenMade) {
      this.suggestionsOpen = false;
      this.hasSelectionBeenMade = false; // Reset for next search
    }

    this.hide = isHeCustomStoreCart(state) || !!isHePrivateStoreCart(state);
    this._setSearchPlaceholder();
  }

  /**
   * Checks if the search component or any of its child elements has focus
   * This helps support both direct usage and usage within tabbed-search component
   */
  private _hasElementFocus(): boolean {
    const activeElement = document.activeElement;

    // Check if the active element is inside the shadow DOM
    if (this.shadowRoot?.activeElement) {
      return true;
    }

    // Check if the active element is the input element within this component
    if (activeElement && this.inputElement() === activeElement) {
      return true;
    }

    // Check if the active element is inside this component
    return activeElement !== null &&
      (this.contains(activeElement) ||
        this.shadowRoot?.contains(activeElement) ||
      activeElement.closest(this.tagName.toLowerCase()) === this);
  }


  /**
   * The most releveant suggestion.
   */
  firstSuggestion() {
    return this.suggestions?.suggestions && this.suggestions?.suggestions.length > 0
      ? this.suggestions?.suggestions[0].terms
      : this.searchQuery;
  }

  /**
   * Whether or not there are term suggestions based on the provided search term.
   */
  hasSuggestions(): boolean {
    return (
      this.suggestions !== undefined &&
      this.suggestions.suggestions !== undefined &&
      this.suggestions.suggestions.length >= 1
    );
  }

  /**
   * Whether or note there are facet value suggestions based on the provided search term.
   */
  hasFacetValueSuggestions(): boolean {
    return (
      this.suggestions !== undefined &&
      this.suggestions.topCategoryFacetValues !== undefined &&
      this.suggestions.topCategoryFacetValues?.length >= 1
    );
  }

  private _categorySuggestionUrl(suggestion: TopCategoryFacetValue) {
    const mapping: any = {
      'Prek-12': 'seg',
      HigherEd: 'he',
    };
    const mappedBU = mapping[suggestion.name] || suggestion.name;

    if (catalogs.includes(this.catalog) && !catalogs.includes(mappedBU)) {
      return generateSearchUrl({
        searchQuery: this.firstSuggestion(),
        businessUnit: this.catalog,
        discipline: suggestion.name,
      });
    } else {
      return generateSearchUrl({ searchQuery: this.firstSuggestion(), businessUnit: mappedBU });
    }
  }

  private _termSuggestionUrl(suggestion: Suggestion) {
    if (catalogs.includes(this.catalog)) {
      return generateSearchUrl({ searchQuery: suggestion.terms, businessUnit: this.catalog });
    } else {
      return generateSearchUrl({ searchQuery: suggestion.terms });
    }
  }

  private _keyPress(event: KeyboardEvent) {
    this.isSearchSubmit = false;
    if ((event.which === 13 || event.keyCode === 13 || event.key === 'Enter') && this.currentFocus == -1) {
      this._search(false);
      this.suggestionsOpen = false;
      this.hasSelectionBeenMade = true;
      if (event.key === 'Enter') {
        this.inputElement().blur();
      }
    }
  }

  private debounceInput(): Function {
    return debounce(this._inputUpdate, 500);
  }

  private _updateSearchQuery() {
    this.searchQuery = this.inputElement().value;
  }

  private _inputUpdate() {
    if (this.searchQuery.length >= 3 && this.useSuggestions) {
      this.currentFocus = -1; // Reset focus when updating input
      this._updateSuggestions();
      const catalogMenuListItems = this.catalogMenuListItems();
      this.unFocusListElement(catalogMenuListItems);
    } else {
      this.suggestionsOpen = false;
      this.currentFocus = -1; // Reset focus when closing suggestions
    }
  }

  private _focus() {
    if (this.searchQuery.length >= 3 && this.useSuggestions) {
      this.suggestionsOpen = true;
      this.currentFocus = -1; // Reset focus when opening suggestions
      const catalogMenuListItems = this.catalogMenuListItems();
      this.unFocusListElement(catalogMenuListItems);
    }
  }

  private _updateSuggestions() {
    store.dispatch(getSuggestions(this._id, this.searchQuery, this.catalog));
  }

  private async _search(isPageLoad: boolean, isSearchSubmit = false) {
    if (isPageLoad) {
      const searchRequest = getSearchRequestFromUrl(this.pageSize) || {};
      this.searchQuery = searchRequest.searchQuery;
      /*
        this is a temparory comment to keep track if we replace isSearchPage() then where the things are breaking
        this.loadResults => this prop is checked if you want to load results on current page.
        based on above configuration we can say that if its checked then thats a search page. so i am removing this check isSearchPage().
      */
      return await this.loadSearchResults(searchRequest);
    } else {
      this.isSearchSubmit = isSearchSubmit;
      this.currentFocus = -1; // Reset focus when searching
      const redirect = this.checkSearchRedirects();
      const state = store.getState() as StoreState;

      emit({
        element: this,
        name: 'search',
        detail: {
          id: this.id,
          term: this.searchQuery,
          redirect: redirect,
          isSearchRedirect: !!redirect,
          store: this.store,
          catalog: this.catalog,
          url: window.location.href,
          user: getUserDataForUserAnalytics(state),
          searchPageType: this.searchPageType,
          searchText: getSearchPageAnalytics(this.searchQuery),
        },
      });

      if (redirect) {
        window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
      } else if (!this.loadResults) {
        this.isLoading = true;
        const { programSourceId, urlToGo } = await this.getIsbnToRedirect(mapCatalog(this.catalog), this.searchQuery, this.plpSearch) 
          ?? { programSourceId: "", urlToGo: "" };
        if (urlToGo) {
          if (!checkBUParam()) {
            savePdpLocalStorageObject({
              searchComponentQuery: true,
              searchTerm: this.searchQuery, 
              programSourceId,
            });
          }
          this.goToPage(appendQueryParamToUrl(urlToGo, productDiscoveryQPKey, productDiscoveryQPValue));
          return;
        }
        this.goToSearchPage();
      } else if (this.loadResults) {
        await this.loadSearchResults({});
      } else {
        this.goToSearchPage();
      }
    }
  }

  private async getIsbnToRedirect(catalog, isbn, isPlpSearch) {
    if (isPlpSearch) {
      catalog = Catalog.seg_product;
    }

    return performSearchSpecificIsbn({ catalog, predefinedFilters: this.predefinedFilters, searchTerms: isbn });
  }

  private goToSearchPage() {
    this.goToPage(generateSearchUrl({ searchQuery: this.searchQuery, businessUnit: this.catalog }));
  }

  private goToPage(url: string) {
    window.location.href = url;
  }

  /**
   * Sends a 'search' event to the data layer with relevant data.
   *
   * @param supressEmit If true, no data layer event will be logged
   */
  private checkSearchRedirects(catalog?: string) {
    if (!this.searchTermRedirects?.redirects) {
      return;
    }

    const state = store.getState() as StoreState;
    this.selectedCatalog = selectSelectedCatalog(state);
    const redirect = this.searchTermRedirects.redirects.find((redirect) => {
      return (
        this.searchQuery.toLowerCase() === redirect.searchTerm.toLowerCase() &&
        [catalog, mapCatalog(this.catalog), this.selectedCatalog, 'ALL'].includes(redirect.catalog)
      );
    });

    return redirect?.target;
  }

  async loadSearchResults({
    searchQuery = '',
    page = 1,
    sortby = '',
    catalog = '',
    fieldValueFilter = {},
    isCatalogSet = false,
  }) {
    const isFieldValueFilterAvailable = Object.keys(fieldValueFilter).length > 0;
    const state = store.getState() as StoreState;
    const currentSort = selectSortBy(state);
    const searchCatalog = mapCatalog(catalog || this.catalog);

    const result = await store
      .dispatch(
      performSearch({
        searchTerms: searchQuery || this.searchQuery,
        isPlpSearch: this.plpSearch,
        catalog: searchCatalog,
        pageLength: this.pageSize,
        start: page,
        sortBy: sortby || currentSort || 'relevance',
        facets: true,
        store: this.store,
          fieldValueFilter: isFieldValueFilterAvailable ? fieldValueFilter : this.filterValues(searchCatalog),
          excludeFacetName: undefined,
          clearFilter: this.plpSearch ? false : true,
          clearSort: this.plpSearch ? false : true,
          isCatalogSet: isCatalogSet,
          predefinedFilters: this.predefinedFilters
        })
      );

    const totalResults = result ? result.total: 0;
    
    if (this.searchQuery) {
      emit({
        element: this,
        name: 'search-results',
        detail: {
          id: this.id,
          term: this.searchQuery,
          redirect: false,
          isSearchRedirect: false,
          store: this.store,
          url: window.location.href,
          user: getUserDataForUserAnalytics(state),
          totalResults,
          searchPageType: this.searchPageType,
          catalog: selectSelectedCatalog(state),
          searchText: getSearchPageAnalytics(this.searchQuery, totalResults),
        },
      });
    }
    this._saveSearchLSObject(totalResults);
  }

  filterValues(searchCatalog): { [key: string]: Array<string> } {
    const state: StoreState = store.getState() as StoreState;
    const appliedFilter: { [key: string]: Array<string> } = {};
    const selectedCatalog =  selectSelectedCatalog(state);
    if (searchCatalog == selectedCatalog) {
      const facets: Array<SearchFacet> = selectFacets(state)!;
      facets?.forEach((facet) => {
        const values: Array<string> = facet?.facetValues
          .filter((facetValue) => facetValue.selected)
          .map((val) => val.name);
        if (values.length > 0) {
          appliedFilter[facet.name] = values;
        }
      });
    }

    return appliedFilter;
  }

  /* 
    TODO this function need refactoring 
    we are using - 
    catalog - its a local variable whose value is coming form suggestionToCatalogMap (which is a constant).
    this.catalog - its prop that being pass by parent component
    Problem is - 
    1) why we need 2 different variable?
    2) Is there any case where catalog value and this.catalog can be different?
  */
  private async _searchForCategorySuggestion(e: Event, suggestion: TopCategoryFacetValue) {
    this.searchQuery = this.firstSuggestion();
    this.hasSelectionBeenMade = true;
    this.currentFocus = -1; // Reset focus when selecting a suggestion
    const suggestionToCatalogMap = {
      'Prek-12': 'SEG_PROGRAM',
      'content': 'CONTENT',
      'HigherEd': 'HE',
      'Professional': 'PROFESSIONAL',
      'products': 'PRODUCTS',
      'articles': 'ARTICLES'
    };
    const catalog = suggestionToCatalogMap[suggestion.name];
    const redirect = this.checkSearchRedirects(catalog);
    const searchCatalog = mapCatalog(catalog || this.catalog);
    emit({
      element: this,
      name: 'searched-for-category-suggestion',
      detail: {
        term: this.searchQuery,
        redirect: redirect,
        isSearchRedirect: !!redirect,
        searchPageType: this.searchPageType,
        catalog:  searchCatalog,
        searchText: getSearchPageAnalytics(this.searchQuery),
      },
      dataLayer: true,
    });
    let totalResults;

    if (redirect && this.catalog.toUpperCase() === Catalog.unified) {
      e.preventDefault();
      window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
    } else if (this.loadResults) {
      e.preventDefault();
      if (catalog) {
        store.dispatch(setSelectedCatalog(catalog));
        store.dispatch(clearSuggestions());
        await this.loadSearchResults({});
      } else {
        const params = {
          addFacetName: DISCIPLINE_PRODUCT_TAG_PATH,
          addFacetValue: suggestion.name,
          excludeFacetName: DISCIPLINE_PRODUCT_TAG_PATH,
          isPlpSearch: this.plpSearch,
          searchTerms: this.searchQuery,
          start: 1,
          predefinedFilters: this.predefinedFilters,
        };
        const result = await store.dispatch(performSearch(params));
        totalResults = result ? result.total: 0;
        this._saveSearchLSObject(totalResults);
      }
    }
    this._closeSuggestions(e);
  }

  private async _searchForTermSuggestion(e: Event, term: string) {
    this.searchQuery = term;
    this.hasSelectionBeenMade = true;
    this.currentFocus = -1; // Reset focus when selecting a term suggestion
    const redirect = this.checkSearchRedirects(this.catalog);
    emit({
      element: this,
      name: 'searched-for-term-suggestion',
      detail: {
        term: this.searchQuery,
        searchPageType: this.searchPageType,
        catalog: this.catalog
      },
      dataLayer: true,
    });

    if (redirect) {
      e.preventDefault();
      window.location.href = appendQueryParamToUrl(redirect, productDiscoveryQPKey, productDiscoveryQPValue);
    } else if (this.loadResults) {
      e.preventDefault();
      await this.loadSearchResults({});
    }
    this.suggestionsOpen = false;
  }

  private inputElement(): HTMLInputElement {
    return this.shadowRoot!.getElementById('input-search-box')! as HTMLInputElement;
  }

  /**
   * Keyboard navigation of autocomplete-catalog-menu. Triggered for keydown event on us-autocomplete-pro-catalog-input
   */
  private _catalogMenuKeyboardNav(e: KeyboardEvent) {
    const catalogMenuListItems = this.catalogMenuListItems();
    if (catalogMenuListItems.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          this.currentFocus++;
          this.focusListElement(catalogMenuListItems);
          break;
        case 'ArrowUp':
          this.currentFocus--;
          this.focusListElement(catalogMenuListItems);
          break;
        case 'Enter':
          // Check if currentFocus is valid and an element exists at that index
          if (this.currentFocus >= 0 && this.currentFocus < catalogMenuListItems.length) {
            catalogMenuListItems[this.currentFocus].click();
            this.hasSelectionBeenMade = true;
            this.inputElement().blur();
          }
          break;
        case 'Escape':
          this.suggestionsOpen = false;
          break;
      }
    }
  }

  private catalogMenuListItems(): HTMLCollectionOf<HTMLElement> {
    return this.shadowRoot!.querySelector('#' + this.id + '-autocomplete-catalog-menu')?.getElementsByTagName(
      'a'
    )! as HTMLCollectionOf<HTMLElement>;
  }

  /**
   * Focus on autocomplete-catalog-menu li element hovered by keyboard up or down keys.
   * @param catalogMenuListItems
   */
  private focusListElement(catalogMenuListItems: HTMLCollectionOf<HTMLElement>) {
    this.unFocusListElement(catalogMenuListItems);
    if (this.currentFocus >= catalogMenuListItems.length) this.currentFocus = 0;
    if (this.currentFocus < 0) this.currentFocus = catalogMenuListItems.length - 1;
    catalogMenuListItems[this.currentFocus].classList.add('bg-light');
    catalogMenuListItems[this.currentFocus].setAttribute('aria-selected', 'true');
  }

  /**
   * Un-Focus on autocomplete-catalog-menu li element hovered by keyboard up or down keys.
   * @param catalogMenuListItems
   */
  private unFocusListElement(catalogMenuListItems: HTMLCollectionOf<HTMLElement>) {
    for (let i = 0; i < catalogMenuListItems.length; i++) {
      catalogMenuListItems[i].classList.remove('bg-light');
      catalogMenuListItems[i].setAttribute('aria-selected', 'false');
    }
  }

  private _prePopulateSearchLSValues() {
    const resetSearchLSKey = SearchTermAnalyticsConstants.RESET_SEARCH_LOCAL_STORAGE;
    const professionalProductsMapperKey = SearchTermAnalyticsConstants.RELATED_PRODUCTS_MAPPER;
    try {
      if (localStorage.getItem(resetSearchLSKey) === null) {
        localStorage.setItem(resetSearchLSKey, 'false');
      }
      if (localStorage.getItem(professionalProductsMapperKey) === null) {
        localStorage.setItem(professionalProductsMapperKey, JSON.stringify({}));
      }
    } catch (error) {
      console.error('Error in _prePopulateSearchLSValues.');
    }
  }

  private _saveSearchLSObject(totalResults: number) {
    if (!checkBUParam()) {
      saveSearchLocalStorageObject({
        searchTerm: this.searchQuery ? this.searchQuery : 'no query',
        totalResults
      });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search': Search;
  }
}